import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUserCredits } from '@/hooks/useUserCredits';
import { useInfluencerAccess } from '@/hooks/useInfluencerAccess';
import { useAnalytics } from '@/hooks/useAnalytics';
import type { TreslogEntry, TreslogEntryInsert, TreslogEntryUpdate } from '@/types/treslog';

export function useTreslog() {
  const { user } = useAuth();
  const { credits } = useUserCredits();
  const { trackTreslogEntryStarted, trackTreslogEntrySaved } = useAnalytics();
  
  const [entries, setEntries] = useState<TreslogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { hasInfluencerAccess } = useInfluencerAccess();

  // Check if user has Legacy plan access
  const hasLegacyAccess = credits?.highest_bundle_purchased === 'legacy' || hasInfluencerAccess;

  const fetchEntries = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);

    // Use raw query since types may not be updated yet
    const { data, error: fetchError } = await (supabase as any)
      .from('treslog_entries')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (fetchError) {
      console.error('Error fetching treslog entries:', fetchError);
      setError('Failed to load entries');
    } else {
      setEntries((data as TreslogEntry[]) || []);
    }
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    if (user && hasLegacyAccess) {
      fetchEntries();
    } else {
      setIsLoading(false);
    }
  }, [user, hasLegacyAccess, fetchEntries]);

  const createEntry = async (entry: TreslogEntryInsert): Promise<TreslogEntry | null> => {
    if (!user || !hasLegacyAccess) return null;

    trackTreslogEntryStarted('manual');

    const { data, error: insertError } = await (supabase as any)
      .from('treslog_entries')
      .insert({
        ...entry,
        user_id: user.id,
      })
      .select()
      .single();

    if (insertError) {
      console.error('Error creating treslog entry:', insertError);
      throw new Error('Failed to create entry');
    }

    const newEntry = data as TreslogEntry;
    trackTreslogEntrySaved(entry.content.split(/\s+/).length);
    setEntries((prev) => [newEntry, ...prev]);
    return newEntry;
  };

  const updateEntry = async (id: string, updates: TreslogEntryUpdate): Promise<TreslogEntry | null> => {
    if (!user || !hasLegacyAccess) return null;

    const { data, error: updateError } = await (supabase as any)
      .from('treslog_entries')
      .update(updates)
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single();

    if (updateError) {
      console.error('Error updating treslog entry:', updateError);
      throw new Error('Failed to update entry');
    }

    const updatedEntry = data as TreslogEntry;
    if (updates.content) {
      trackTreslogEntrySaved(updates.content.split(/\s+/).length);
    }
    setEntries((prev) => prev.map((e) => (e.id === id ? updatedEntry : e)));
    return updatedEntry;
  };

  const deleteEntry = async (id: string): Promise<boolean> => {
    if (!user || !hasLegacyAccess) return false;

    const { error: deleteError } = await (supabase as any)
      .from('treslog_entries')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (deleteError) {
      console.error('Error deleting treslog entry:', deleteError);
      throw new Error('Failed to delete entry');
    }

    setEntries((prev) => prev.filter((e) => e.id !== id));
    return true;
  };

  const uploadPhoto = async (file: File): Promise<string | null> => {
    if (!user) return null;

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('treslog-photos')
      .upload(fileName, file);

    if (uploadError) {
      console.error('Error uploading photo:', uploadError);
      throw new Error('Failed to upload photo');
    }

    const { data: urlData } = supabase.storage
      .from('treslog-photos')
      .getPublicUrl(fileName);

    return urlData.publicUrl;
  };

  return {
    entries,
    isLoading,
    error,
    hasLegacyAccess,
    createEntry,
    updateEntry,
    deleteEntry,
    uploadPhoto,
    refetch: fetchEntries,
  };
}
